import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import {
  Box,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Check,
  X,
  RefreshCw,
  Database,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Textarea } from '../../../../components/ui/textarea';
import { Switch } from '../../../../components/ui/switch';
import { cn } from '../../../../lib/utils';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const PlatformObjectsPage = () => {
  const { token } = useAuth();
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [seeding, setSeeding] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchObjects();
  }, [token]);

  const fetchObjects = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/settings/data/objects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObjects(res.data.objects || []);
    } catch (err) {
      console.error('Failed to fetch objects:', err);
      toast.error('Failed to load objects');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/platform/settings/data/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Seeded ${res.data.seeded.objects} objects, ${res.data.seeded.custom_fields} fields, ${res.data.seeded.custom_values} values`);
      fetchObjects();
    } catch (err) {
      toast.error('Failed to seed defaults');
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.key) {
      toast.error('Name and key are required');
      return;
    }

    try {
      if (editingObject) {
        await axios.put(`${API}/platform/settings/data/objects/${editingObject.id}`, {
          name: formData.name,
          description: formData.description,
          is_active: formData.is_active
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Object updated');
      } else {
        await axios.post(`${API}/platform/settings/data/objects`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Object created');
      }
      setShowAddModal(false);
      setEditingObject(null);
      resetForm();
      fetchObjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save object');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      key: '',
      description: '',
      is_active: true
    });
  };

  const openEditModal = (obj) => {
    setEditingObject(obj);
    setFormData({
      name: obj.name,
      key: obj.key,
      description: obj.description || '',
      is_active: obj.is_active
    });
    setShowAddModal(true);
  };

  const filteredObjects = objects.filter(obj =>
    obj.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    obj.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0B1F18] p-8" data-testid="platform-objects-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
            <Box className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Objects</h1>
            <p className="text-[#93AE9F] text-sm">
              Manage object types used across custom fields, workflows, and automations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleSeedDefaults}
            disabled={seeding}
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          >
            {seeding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
            Seed Defaults
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setEditingObject(null);
              setShowAddModal(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="add-object-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Object
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            placeholder="Search objects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-700 text-white"
          />
        </div>
      </div>

      {/* Objects Table */}
      <div className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
          </div>
        ) : filteredObjects.length === 0 ? (
          <div className="text-center py-12">
            <Box className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Objects Found</h3>
            <p className="text-[#93AE9F] text-sm mb-4">
              {objects.length === 0 
                ? 'Click "Seed Defaults" to populate default platform objects.'
                : 'No objects match your search criteria.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-zinc-900/30">
              <tr className="text-left">
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Key</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Description</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-[#93AE9F] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(142,218,182,0.08)]">
              {filteredObjects.map((obj) => (
                <tr key={obj.id} className="hover:bg-zinc-900/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Box className="w-4 h-4 text-emerald-400" />
                      </div>
                      <span className="font-medium text-white">{obj.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-sm bg-zinc-800 text-emerald-400 px-2 py-1 rounded">{obj.key}</code>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#93AE9F] max-w-xs truncate">
                    {obj.description || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {obj.is_system ? (
                      <Badge className="bg-purple-500/20 text-purple-400 border-0">System</Badge>
                    ) : (
                      <Badge className="bg-zinc-600/50 text-zinc-400 border-0">Custom</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {obj.is_active ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-zinc-600/50 text-zinc-400 border-0">
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {!obj.is_system && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(obj)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-400 mb-1">About Platform Objects</h4>
            <p className="text-sm text-blue-300/80">
              Objects define the data types that can have custom fields, tags, and scoring rules. 
              System objects cannot be modified but can be toggled on/off. Custom objects can be 
              created for specialized use cases.
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingObject ? 'Edit Object' : 'Add Object'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Custom Entity"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Key *</label>
              <Input
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                placeholder="e.g., custom_entity"
                className="bg-zinc-800 border-zinc-700 text-white"
                disabled={!!editingObject}
              />
              <p className="text-xs text-zinc-500 mt-1">Used as the identifier in templates and workflows</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this object type..."
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-white">Active</label>
                <p className="text-xs text-zinc-500">Enable this object for use in the platform</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setEditingObject(null);
                resetForm();
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {editingObject ? 'Save Changes' : 'Create Object'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformObjectsPage;
